import Phaser from 'phaser';
import { callGameStart } from '../utils/gamebox';

export class BootScene extends Phaser.Scene {
  private loadingImage!: Phaser.GameObjects.Image;
  private progressBar!: Phaser.GameObjects.Graphics;
  private progressBarBg!: Phaser.GameObjects.Graphics;
  private progressText!: Phaser.GameObjects.Text;
  private barWidth!: number;
  private barHeight!: number;
  private barX!: number;
  private barY!: number;

  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    const { width, height } = this.scale;

    // ローディング画面の背景
    this.cameras.main.setBackgroundColor('#799EFF');

    // loading.pngを最初にロード（他のアセットより先に）
    const pathname = window.location.pathname;
    const basePath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname.split('/').slice(0, -1).join('/') || '';
    const assetsPath = basePath ? `${basePath}/assets` : '/assets';
    
    this.load.image('loading', `${assetsPath}/images/loading.png`);
    
    // プログレスバーのサイズと位置を計算
    this.barWidth = width * 0.6;
    this.barHeight = 30;
    this.barX = width / 2 - this.barWidth / 2;
    this.barY = height / 2;
    
    // loading画像のロード完了を待ってから表示（進捗バーの少し上、リサイズせず使用）
    this.load.once('filecomplete-image-loading', () => {
      this.loadingImage = this.add.image(width / 2, this.barY - 50, 'loading');
      this.loadingImage.setDepth(10);
    });

    // プログレスバーの背景
    this.progressBarBg = this.add.graphics();
    this.progressBarBg.fillStyle(0x000000, 0.5);
    this.progressBarBg.fillRect(this.barX, this.barY, this.barWidth, this.barHeight);

    // プログレスバー
    this.progressBar = this.add.graphics();

    // 進捗テキスト（フォントが読み込まれるまで待機）
    const createProgressText = () => {
      this.progressText = this.add
        .text(width / 2, height / 2 + 50, '0%', {
          fontSize: '40px',
          fontFamily: 'Micro5',
          color: '#ffffff',
        })
        .setOrigin(0.5);
    };

    // フォントが読み込まれるまで待機
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        createProgressText();
      });
    } else {
      // フォントAPIが利用できない場合は即座に作成
      createProgressText();
    }

    // 進捗イベント
    this.load.on('progress', (value: number) => {
      if (this.progressBar && this.progressText) {
        this.progressBar.clear();
        this.progressBar.fillStyle(0xffff00, 1);
        this.progressBar.fillRect(this.barX, this.barY, this.barWidth * value, this.barHeight);
        this.progressText.setText(`${Math.round(value * 100)}%`);
      }
    });

    this.load.on('complete', () => {
      if (this.progressBar && this.progressText) {
        this.progressBar.clear();
        this.progressBar.fillStyle(0x00ff00, 1);
        this.progressBar.fillRect(this.barX, this.barY, this.barWidth, this.barHeight);
        this.progressText.setText('100%');
        // Complete!の画像があれば切り替え、なければそのまま
        if (this.textures.exists('loading_complete')) {
          this.loadingImage.setTexture('loading_complete');
        }
      }
    });
    // アセットの読み込み
    // ピッチャーのスプライトシートをロード（885x150ピクセル、5コマ横並び、1コマ177x150ピクセル）
    // 177×5 = 885なので、5コマすべてが読み込まれる
    // 実際の画像サイズに合わせて設定（画像が155ピクセルの場合は155に変更）
    // pathname, basePath, assetsPathは既に上で宣言済み
    
    this.load.spritesheet('pitcher_ss', `${assetsPath}/images/pitcher_ss.png`, {
      frameWidth: 177, // 1フレームの幅（177ピクセル）
      frameHeight: 150, // 1フレームの高さ（150ピクセル、リサイズせず使用）
    });
    // バッターのスプライトシートをロード（1コマ137x162ピクセル）
    this.load.spritesheet('batter_ss', `${assetsPath}/images/batter_ss.png`, {
      frameWidth: 137, // 1フレームの幅（137ピクセル）
      frameHeight: 162, // 1フレームの高さ（162ピクセル、リサイズせず使用）
    });
    this.load.image('catcher', `${assetsPath}/images/catcher.png`);
    this.load.image('ground', `${assetsPath}/images/ground.png`);
    this.load.image('ball', `${assetsPath}/images/ball.png`);
    this.load.image('stop', `${assetsPath}/images/stop.png`);
    this.load.image('play', `${assetsPath}/images/play.png`);
    this.load.image('playball', `${assetsPath}/images/playball.png`);
    this.load.image('playball_hover', `${assetsPath}/images/playball_hover.png`);
    this.load.image('bg', `${assetsPath}/images/bg.png`);
    this.load.image('bg_top', `${assetsPath}/images/bg_top.png`);
    this.load.image('bg_result', `${assetsPath}/images/bg_result.png`);
    // loading.pngは既に最初にロード済み
    this.load.image('logo', `${assetsPath}/images/logo.png`);
    this.load.image('start', `${assetsPath}/images/start.png`);
    this.load.image('start_hover', `${assetsPath}/images/start_hover.png`);
    this.load.image('how-to-play', `${assetsPath}/images/how-to-play.png`);
    this.load.image('back_to_title', `${assetsPath}/images/back_to_title.png`);
    this.load.image('back_to_title_hover', `${assetsPath}/images/back_to_title_hover.png`);
    this.load.image('angle', `${assetsPath}/images/angle.png`);
    this.load.image('timing_bar', `${assetsPath}/images/timing_bar.png`);
    this.load.image('fence', `${assetsPath}/images/fence.png`);
    this.load.image('sounds_on', `${assetsPath}/images/sounds_on.png`);
    this.load.image('sounds_off', `${assetsPath}/images/sounds_off.png`);

    // 音声ファイルの読み込み
    this.load.audio('bgm', `${assetsPath}/sounds/bgm.mp3`);
    this.load.audio('ball_hit', `${assetsPath}/sounds/ball_hit.wav`);
    this.load.audio('button', `${assetsPath}/sounds/button.mp3`);
    this.load.audio('pitch', `${assetsPath}/sounds/pitch.wav`);
    this.load.audio('swing', `${assetsPath}/sounds/swing.wav`);
    this.load.audio('cheers', `${assetsPath}/sounds/cheers.wav`);
    this.load.audio('result', `${assetsPath}/sounds/result.mp3`);
    this.load.audio('fence', `${assetsPath}/sounds/fence.wav`);
    this.load.audio('over_score', `${assetsPath}/sounds/over_score.mp3`);
  }

  create(): void {
    // アセット読み込み完了後、TitleSceneに遷移
    // （game_startはTitleSceneの「Tap to Start」ボタンタップ時に呼ばれる）
    this.time.delayedCall(500, () => {
      this.scene.start('TitleScene');
    });
  }
}
